/**
 * Cookie utility functions for session management and GDPR compliance
 * Provides secure cookie handling with expiration and cross-tab sync
 */

interface CookieOptions {
  days?: number;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Set a cookie with optional configuration
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const {
    days = 7, // Default 7 days
    path = '/',
    secure = window.location.protocol === 'https:',
    sameSite = 'lax'
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    cookieString += `; expires=${date.toUTCString()}`;
  }
  
  cookieString += `; path=${path}`;
  
  if (secure) {
    cookieString += '; secure';
  }
  
  cookieString += `; SameSite=${sameSite}`;
  
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  
  return null;
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string, path: string = '/'): void {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Set session token with automatic expiration
 */
export function setSessionToken(token: string, rememberMe: boolean = false): void {
  const days = rememberMe ? 30 : 7; // 30 days for "Remember Me", 7 days otherwise
  setCookie('cg_session_token', token, { days });
  
  // Also store expiration time
  const expirationTime = new Date().getTime() + (days * 24 * 60 * 60 * 1000);
  setCookie('cg_session_expiry', expirationTime.toString(), { days });
}

/**
 * Get session token if valid
 */
export function getSessionToken(): string | null {
  const token = getCookie('cg_session_token');
  const expiry = getCookie('cg_session_expiry');
  
  if (!token || !expiry) {
    return null;
  }
  
  // Check if session has expired
  const expirationTime = parseInt(expiry, 10);
  if (new Date().getTime() > expirationTime) {
    // Session expired, clear cookies
    clearSession();
    return null;
  }
  
  return token;
}

/**
 * Clear session cookies
 */
export function clearSession(): void {
  deleteCookie('cg_session_token');
  deleteCookie('cg_session_expiry');
  deleteCookie('cg_user_id');
  deleteCookie('cg_remember_me');
}

/**
 * Refresh session expiration (called on user activity)
 */
export function refreshSession(): void {
  const token = getCookie('cg_session_token');
  const rememberMe = getCookie('cg_remember_me') === 'true';
  
  if (token) {
    setSessionToken(token, rememberMe);
  }
}

/**
 * Check if session is valid
 */
export function isSessionValid(): boolean {
  return getSessionToken() !== null;
}